"use client";
/**
 * One line of a bounded catalog page, as four declared columns rather than a
 * dozen independent flex negotiations.
 *
 *   rank · identity 1fr · state 74 · action 84
 *
 * with a DECLARED truncation order of `difficulty → equipment → title`. The
 * first two steps are the meta line's own and are measured by the kit's fact
 * line, which drops from the tail in the order this family declares and stamps
 * how many it dropped. The title is last, because on a page of one exercise
 * type — the mounted page is three yoga poses — the name is the only field that
 * differs on every row: `bodyweight` and `beginner` are identical across all
 * three.
 *
 * A catalog row publishes three video BOOLEANS and no poster URL; a picture
 * only exists after a second, billed per-exercise call. The inline lead keeps
 * a provided poster when one has been resolved, otherwise the row's own index.
 * Its state capsule belongs on the wider row: at 340px the fixed column cuts
 * the state word and takes the room the exercise name needs.
 */
import type { ReactElement } from "react";
import {
  CresultcardIdentity,
  CresultcardMedia,
  CresultcardMediaKind,
  CresultcardMediaScale,
  useCresultcardInsideMini,
  CresultcardRow,
  CresultcardIdentitySlot,
  CresultcardRowIdentityFloor,
  CresultcardTitleLines,
} from "@/components/ui/Cresultcard";
import {
  C_WORKOUT_ROW_FIGURE_GAP,
  C_WORKOUT_ROW_STATE,
  C_WORKOUT_ROW_VARIANT,
} from "./cworkoutcard.constants";
import { CworkoutcardMetaLine } from "./CworkoutcardMeta";
import { CworkoutcardStateCapsule } from "./CworkoutcardStateCapsule";
import type { CworkoutcardRowProps } from "./cworkoutcard.types";

/** A provided poster identifies the movement without becoming a player. */
const INLINE_POSTER_SIZE = 40;
/** The title yields last, and only after the meta line has dropped its own. */
const ROW_TRUNCATION: readonly CresultcardIdentitySlot[] = [
  CresultcardIdentitySlot.Title,
];

export function CworkoutcardRow({
  data,
  rank,
  onLast = false,
  openControl,
}: CworkoutcardRowProps): ReactElement {
  const insideMini = useCresultcardInsideMini();
  const poster = data.media?.clips.find((clip) => clip.poster != null)?.poster;
  return (
    <CresultcardRow
      figure={insideMini ? undefined :
        <span
          data-el="Cworkoutcard/Row/Status"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: C_WORKOUT_ROW_FIGURE_GAP,
            width: C_WORKOUT_ROW_STATE,
            flex: "0 0 auto",
            minWidth: 0,
          }}
        >
          <CworkoutcardStateCapsule
            data={data}
          />
        </span>
      }
      identity={
        // `/Identity/Block` rather than `/Identity`: the kit's row already
        // stamps its own identity ZONE with that path, and two elements
        // answering to one debug path is a measurement that cannot be taken.
        <CresultcardIdentity
          openControl={openControl}
          title={data.title}
          titleLines={CresultcardTitleLines.Two}
          truncationOrder={ROW_TRUNCATION}
        />
      }
      identityFloor={CresultcardRowIdentityFloor.Title}
      lead={insideMini && poster != null ? (
        <span style={{ display: "block", width: INLINE_POSTER_SIZE }}>
          <CresultcardMedia
            alt={data.title}
            fallbackTitle={data.title}
            intrinsicHeight={poster.intrinsicHeight}
            intrinsicWidth={poster.intrinsicWidth}
            kind={CresultcardMediaKind.Photograph}
            scale={CresultcardMediaScale.Thumb}
            src={poster.src}
          />
        </span>
      ) : undefined}
      meta={
        <CworkoutcardMetaLine
          data={data}
        />
      }
      onLast={onLast}
      openHint
      rank={rank}
      variant={C_WORKOUT_ROW_VARIANT}
    />
  );
}
